import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PrivacyTip,
  Analytics,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { initAnalytics } from '../services/analytics.ts';

interface ConsentManagerProps {
  measurementId?: string;
  onConsentChange?: (consent: boolean) => void;
}

const ConsentManager: React.FC<ConsentManagerProps> = ({ 
  measurementId, 
  onConsentChange 
}) => {
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(() => {
    // Read from localStorage on first render
    const stored = localStorage.getItem('analytics-consent');
    return stored === 'true';
  });
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('analytics-consent');
    
    if (consent !== null) {
      const hasConsent = consent === 'true';
      setAnalyticsConsent(hasConsent);
      setHasConsented(true);
      
      if (hasConsent && measurementId) {
        console.log('🔍 ConsentManager: Initializing analytics with stored consent');
        initAnalytics(measurementId);
      } else if (hasConsent && !measurementId) {
        console.warn('⚠️ ConsentManager: Consent given but no measurement ID available');
      }
    } else {
      // Show consent dialog if no decision has been made
      setShowConsentDialog(true);
    }
  }, [measurementId]);

  const handleConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnalyticsConsent(event.target.checked);
  };

  const handleAccept = () => {
    console.log('🔍 Consent Accepted - Initializing Analytics...');
    localStorage.setItem('analytics-consent', 'true');
    setAnalyticsConsent(true);
    setHasConsented(true);
    setShowConsentDialog(false);
    
    if (measurementId) {
      console.log('🔍 Calling initAnalytics with ID:', measurementId);
      initAnalytics(measurementId);
    } else {
      console.warn('⚠️ No measurement ID provided to ConsentManager');
    }
    
    onConsentChange?.(true);
  };

  const handleDecline = () => {
    localStorage.setItem('analytics-consent', 'false');
    setAnalyticsConsent(false);
    setHasConsented(true);
    setShowConsentDialog(false);
    
    onConsentChange?.(false);
  };

  const handleUpdateConsent = () => {
    setShowConsentDialog(true);
  };

  return (
    <>
      {/* Consent Dialog */}
      <Dialog 
        open={showConsentDialog} 
        onClose={handleDecline}
        maxWidth="md"
        fullWidth
        aria-labelledby="consent-dialog-title"
        aria-describedby="consent-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle 
          id="consent-dialog-title"
          sx={{ 
            pb: 1,
            background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <PrivacyTip sx={{ fontSize: 32, mr: 2 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Privacy & Analytics Consent
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent 
          id="consent-dialog-description"
          sx={{ p: 4 }}
        >
          <Typography variant="body1" paragraph sx={{ mb: 3, fontSize: '1.1rem' }}>
            We respect your privacy and are committed to protecting your personal data. 
            This consent form explains how we use analytics to improve your experience 
            and what data we collect.
          </Typography>
          
          <Typography variant="body2" paragraph sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary' }}>
            Note: This app uses your browser's local storage to remember your preferences (theme, converter settings, consent). 
            This data stays on your device and is essential for the app to function properly.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Analytics sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                What we collect (with your consent):
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
              <li>Pages you visit and features you use</li>
              <li>General usage patterns (anonymized)</li>
              <li>Device and browser information for compatibility</li>
              <li>App performance metrics to identify issues</li>
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Block sx={{ fontSize: 24, mr: 1, color: 'error.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                What we NEVER collect:
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
              <li>Personal information (name, email, address)</li>
              <li>Specific values you convert or calculate</li>
              <li>Your exact location or IP address</li>
              <li>Any data that could identify you personally</li>
            </Box>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Local Storage Usage:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • We use your browser's local storage to remember your preferences
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • This includes your theme choice, consent decision, and converter settings
            </Typography>
            <Typography variant="body2">
              • This data stays on your device and is never sent to our servers
            </Typography>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Your Rights Under GDPR:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • You can withdraw consent at any time using the privacy icon
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • You can request access to or deletion of your data
            </Typography>
            <Typography variant="body2">
              • Analytics data is retained for 26 months by Google Analytics
            </Typography>
          </Box>
          
          <Typography variant="body2" paragraph sx={{ mb: 3 }}>
            By giving consent, you agree to our data collection practices. 
            You can change your consent at any time using the privacy icon in the bottom right corner. 
            For detailed information, please read our{' '}
            <Link 
              href="/privacy-policy.html" 
              target="_blank" 
              rel="noopener noreferrer"
              underline="hover" 
              sx={{ fontWeight: 600 }}
            >
              Privacy Policy
            </Link>.
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={analyticsConsent}
                onChange={handleConsentChange}
                color="primary"
                aria-describedby="consent-checkbox-description"
                sx={{
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  }
                }}
              />
            }
            label="I consent to analytics tracking and understand that the app uses local storage to remember my preferences. I can withdraw this consent at any time."
            sx={{ 
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                fontSize: '1rem',
                fontWeight: 500,
                lineHeight: 1.5,
              }
            }}
          />
          <Typography id="consent-checkbox-description" variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Check this box to consent to analytics tracking. You can change this setting at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleDecline} 
            variant="outlined"
            startIcon={<Block />}
            aria-label="Decline analytics consent"
            sx={{ 
              minWidth: 100,
              borderColor: 'text.secondary',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'text.primary',
                color: 'text.primary',
              },
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'text.primary',
                outlineOffset: '2px',
              }
            }}
          >
            Decline
          </Button>
          <Button 
            onClick={handleAccept} 
            variant="contained" 
            color="primary"
            disabled={!analyticsConsent}
            startIcon={<CheckCircle />}
            aria-label="Accept analytics consent"
            aria-describedby={!analyticsConsent ? "accept-button-description" : undefined}
            sx={{ 
              minWidth: 100,
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
              },
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
              }
            }}
          >
            Accept
          </Button>
          {!analyticsConsent && (
            <Typography id="accept-button-description" variant="caption" color="text.secondary" sx={{ position: 'absolute', top: '100%', right: 0 }}>
              Please check the consent box above to enable this button
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      {/* Floating Privacy Icon */}
      {hasConsented && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
          <Tooltip title="Privacy & Analytics Settings">
            <IconButton
              color="primary"
              size="large"
              aria-label="Open privacy and analytics settings"
              onClick={handleUpdateConsent}
              sx={{ 
                bgcolor: 'background.paper', 
                boxShadow: 3, 
                borderRadius: '50%',
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                }
              }}
            >
              <PrivacyTip />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
};

export default ConsentManager; 